/* 托盤（靈感區）：未編目的素材蓋著硫酸紙。
   兩個隱私細節：
   1. **未掀開就不解碼**：連 object URL 都不建立，所以圖片內容在記憶體裡根本沒被讀出來。
   2. 被標記敏感的素材，在成人區鎖定時由查詢層過濾掉（見 rules.visibleInbox）。 */
import { useEffect, useMemo, useState } from "react";
import type { InboxItem } from "../domain/types.ts";
import { useI18n } from "../i18n/index.tsx";
import { Icon } from "./icons.tsx";

function Thumbnail(props: { item: InboxItem; load: (id: string) => Promise<Blob | null> }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked: string | null = null;
    let alive = true;
    /* 只有掀開過的才去讀內容 —— 這是刻意的：未掀開的素材不該被解碼。 */
    if (props.item.peeled && props.item.kind === "image") {
      void props.load(props.item.id).then((blob) => {
        if (!blob || !alive) return;
        revoked = URL.createObjectURL(blob);
        setUrl(revoked);
      });
    }
    return () => { alive = false; if (revoked) URL.revokeObjectURL(revoked); };
  }, [props.item.id, props.item.peeled, props.item.kind, props.load]);
  if (!url) return <span className="thumb" />;
  return <img className="bagimg" src={url} alt="" />;
}

export function Tray(props: {
  items: InboxItem[]; adultUnlocked: boolean;
  load: (id: string) => Promise<Blob | null>;
  onPeel: (item: InboxItem) => void;
  onToggleSensitive: (item: InboxItem) => void;
  onPress: (item: InboxItem) => void;
  onDelete: (item: InboxItem) => void;
  onPickFiles: () => void;
  hiddenCount: number;
  onGoCreate: () => void;
}) {
  const { t } = useI18n();
  const sorted = useMemo(() => [...props.items].sort((a, b) => b.createdAt - a.createdAt), [props.items]);
  return (
    <div className="tray" style={{ padding: 0 }}>
      <p className="hint" style={{ marginBottom: 12 }}>{t("tray.hint")}</p>
      <div className="row" style={{ marginBottom: 16 }}>
        <button className="btn" data-tray-pick onClick={props.onPickFiles}>
          <Icon name="export" />{t("tray.pick")}</button>
        <span className="hint" style={{ margin: 0 }} data-tray-count>
          {sorted.length} {t("tray.packs")}
          {props.hiddenCount > 0 && "　·　" + t("tray.hidden", { n: props.hiddenCount })}
        </span>
      </div>
      {sorted.length === 0 && <section className="panel"><p className="dim">{t("tray.empty")}</p>
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn" onClick={props.onGoCreate}>{t("tray.goCreate")}</button>
        </div></section>}
      <div className="traygrid">
        {sorted.map((item) => (
          <div className="bag" key={item.id} data-bag={item.id} data-peeled={item.peeled}
            data-sensitive={item.sensitive}>
            <Thumbnail item={item} load={props.load} />
            <span className="glassine" />
            <span className="nm">{item.peeled ? item.name : t("tray.unpeeled")}</span>
            <span className="st mono">{item.peeled
              ? Math.max(1, Math.round(item.bytes / 1024)) + " KB"
              : t("tray.glassine")}</span>
            {item.sensitive && <span className="sensitive mono">{t("tray.sensitive")}</span>}
            <div className="bagactions">
              <button className="chip" data-bag-peel={item.id} onClick={() => props.onPeel(item)}>
                {item.peeled ? t("tray.recover") : t("tray.peel")}</button>
              <button className="chip" data-bag-sensitive={item.id} aria-pressed={item.sensitive}
                onClick={() => props.onToggleSensitive(item)}>{t("tray.mark")}</button>
              <button className="chip" data-bag-press={item.id} onClick={() => props.onPress(item)}>
                {t("tray.press")}</button>
              <button className="chip" data-bag-delete={item.id} onClick={() => props.onDelete(item)}>
                {t("sheet.trash")}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
